import { type MutableRefObject, type TouchEvent, useCallback, useRef } from 'react';
import {
    DRAGGING_ATTRIBUTE,
    FULL_HEIGHT_ATTRIBUTE,
    IGNORE_DRAGS_ATTRIBUTE,
    PREVENT_OVERSCROLL_CLASS,
    setSheetStateAttribute,
} from '../sheetStateAttributes';
import { useAnimationFrameLoop } from '../../../../utils/hooks/useAnimationFrameLoop';
import type { SheetEngineProps } from '../sheetTypes';
import { INITIAL_ANIMATION_STATE, animateSheet, getAnimationStartValues } from '../utils/animateSheetSnap';
import {
    getNewSheetHeight,
    getSnapPointSheetHeight,
    setContentHeightToMax,
    setContentHeightToVisibleSpace,
    setSheetHeight,
    setSheetHeightVariable,
} from '../utils/sheetHeightUtils';
import cancelPressOnDragStart from '../utils/cancelPressOnDragStart';
import { isMoveScroll } from '../utils/sheetScrollUtils';
import { getNewActiveSnapPoint, getSnapPointTopValue } from '../utils/snapPointUtils';

const INITIAL_DRAG_STATE = {
    isDragging: false,
    isScrolling: false,
    isDragCancelled: false,
    hasDoneFirstTouchMove: false,
    sheetTouchOffset: 0,
    currentY: 0,
    velocity: 0,
    touchStartTimestamp: 0,
    prevMoveTimestamp: 0,
    velocities: [],
    latestSheetHeight: 0,
    highestSnapPointPx: 0,
};

export type DragState = {
    isDragging: boolean;
    isScrolling: boolean;
    isDragCancelled: boolean;
    hasDoneFirstTouchMove: boolean;
    sheetTouchOffset: number;
    currentY: number;
    velocity: number;
    touchStartTimestamp: number;
    prevMoveTimestamp: number;
    velocities: number[];
    latestSheetHeight: number;
    highestSnapPointPx: number;
};

export type SheetHandlers = {
    handleSheetTouchStart: (event: TouchEvent) => void;
    handleSheetTouchMove: (event: TouchEvent) => void;
    handleSheetTouchEnd: () => void;
    handleSheetTouchCancel: () => void;
};

/**
 * Handle the touch events on the sheet.
 */
const useSheetHandlers = (
    sheetProps: SheetEngineProps,
    sheetInitialised: boolean,
    snapPointsState: number[],
    cancelInProgressDragAnimationRef: MutableRefObject<(() => void) | null>,
    setIsSheetMounted: (isMounted: boolean) => void,
): SheetHandlers => {
    const {
        activeSnapPoint,
        sheetRef,
        sheetContainerRef,
        onSnapPointChange,
        onCloseTransitionStart,
        onCloseTransitionEnd,
        onOpenChange,
        onDragStart,
        onDragEnd,
        onVelocitySample,
        onDragSettled,
        useViewportResizeForKeyboard = false,
        topInset = 0,
    } = sheetProps;

    const dragState = useRef<DragState>(INITIAL_DRAG_STATE);

    const animationState = useRef(INITIAL_ANIMATION_STATE);
    const animationController = useAnimationFrameLoop(animateSheet(sheetRef, animationState, onVelocitySample));

    /**
     * Handle the touch start event on the sheet content.
     * Get all the starting measurements and set up the timeout to allow
     * the scroll handler to run before we start the drag
     * @param event
     */
    const handleSheetTouchStart = useCallback(
        (event: TouchEvent) => {
            if (!sheetRef.current) return;

            document.querySelector('html')?.classList.add(PREVENT_OVERSCROLL_CLASS);

            const ignoreDrag = (event.target as HTMLElement).closest(`[${IGNORE_DRAGS_ATTRIBUTE}]`) !== null;

            const startY = event.touches[0].clientY;

            dragState.current = {
                ...INITIAL_DRAG_STATE,
                sheetTouchOffset: startY - sheetRef.current.getBoundingClientRect().top,
                currentY: startY,
                touchStartTimestamp: performance.now(),
                prevMoveTimestamp: performance.now(),
                isDragCancelled: ignoreDrag,
                velocities: [],
                latestSheetHeight: 0,

                highestSnapPointPx: getSnapPointTopValue(Math.max(...snapPointsState, activeSnapPoint), topInset),
            };
        },
        [activeSnapPoint],
    );

    /**
     * Handle the touch move event on the sheet content.
     * Check if drag is accepted and if so adjust the sheet height.
     * @param event
     */
    const handleSheetTouchMove = useCallback(
        (event: TouchEvent) => {
            const { prevMoveTimestamp, currentY, hasDoneFirstTouchMove, isDragging, isDragCancelled } =
                dragState.current;

            if (isDragCancelled) return;

            const newY = event.touches[0].clientY;
            const timeNow = performance.now();
            const timeSinceLastMove = timeNow - prevMoveTimestamp;
            if (timeSinceLastMove === 0) return;

            dragState.current.velocity = (currentY - newY) / timeSinceLastMove;
            dragState.current.prevMoveTimestamp = performance.now();
            dragState.current.currentY = newY;
            dragState.current.velocities.push(dragState.current.velocity);
            onVelocitySample?.(dragState.current.velocity, true);

            if (!hasDoneFirstTouchMove) {
                dragState.current.hasDoneFirstTouchMove = true;
                dragState.current.isScrolling = isMoveScroll(event, dragState, sheetRef);
            }

            const shouldBeginDrag = !isDragging && !dragState.current.isScrolling && sheetInitialised;
            if (shouldBeginDrag) {
                dragState.current.isDragging = true;
                // The touch that started the drag may have armed a press on a control it landed on
                cancelPressOnDragStart(event.target);
                setSheetStateAttribute(sheetRef.current, DRAGGING_ATTRIBUTE, true);
                setSheetStateAttribute(sheetRef.current, FULL_HEIGHT_ATTRIBUTE, false);
                setContentHeightToMax(sheetProps);
                if (onDragStart) onDragStart();
            }

            if (!dragState.current.isDragging) return;

            const newSheetHeight = getNewSheetHeight(dragState, dragState.current.highestSnapPointPx);
            if (newSheetHeight === undefined) return;

            dragState.current.latestSheetHeight = newSheetHeight;
            setSheetHeight(sheetRef, newSheetHeight);
        },
        [sheetInitialised],
    );

    /**
     * Handle the touch end event on the sheet content.
     * If the sheet is being dragged, go to the new snap point.
     */
    const handleSheetTouchEnd = useCallback(() => {
        document.querySelector('html')?.classList.remove(PREVENT_OVERSCROLL_CLASS);

        if (!dragState.current.isDragging) return;

        dragState.current.isDragging = false;

        const newActiveSnapPoint = getNewActiveSnapPoint(dragState.current, snapPointsState, topInset);
        const newHeight = getSnapPointSheetHeight(newActiveSnapPoint, topInset);

        const sheetWillClose = newActiveSnapPoint === 0;

        if (sheetWillClose) {
            onCloseTransitionStart?.();
        }

        setSheetStateAttribute(sheetRef.current, FULL_HEIGHT_ATTRIBUTE, newActiveSnapPoint === 1);

        const completeAnimation = () => {
            animationController.stop();
            cancelInProgressDragAnimationRef.current = null;

            if (onDragEnd) onDragEnd();

            onDragSettled?.();

            if (sheetWillClose) {
                onOpenChange(false);
                setIsSheetMounted(false);
                onCloseTransitionEnd?.();
                return;
            }

            onSnapPointChange(newActiveSnapPoint);
            setContentHeightToVisibleSpace(sheetProps, useViewportResizeForKeyboard);
            setSheetHeightVariable(sheetContainerRef, newHeight);
            setSheetStateAttribute(sheetRef.current, DRAGGING_ATTRIBUTE, false);
        };

        cancelInProgressDragAnimationRef.current = () => {
            animationController.stop();
            cancelInProgressDragAnimationRef.current = null;
            setSheetStateAttribute(sheetRef.current, DRAGGING_ATTRIBUTE, false);
        };
        animationState.current = getAnimationStartValues(
            sheetRef,
            dragState,
            newActiveSnapPoint,
            completeAnimation,
            topInset,
        );
        animationController.start();
    }, [snapPointsState, onSnapPointChange, useViewportResizeForKeyboard]);

    /**
     * Handle the touch cancel event on the sheet content.
     * Remove the classes and go to the previous snap point.
     */
    const handleSheetTouchCancel = useCallback(() => {
        setSheetStateAttribute(sheetRef.current, DRAGGING_ATTRIBUTE, false);
        document.querySelector('html')?.classList.remove(PREVENT_OVERSCROLL_CLASS);

        // Reset to original height
        onSnapPointChange(activeSnapPoint);
    }, [activeSnapPoint]);

    return {
        handleSheetTouchStart,
        handleSheetTouchMove,
        handleSheetTouchEnd,
        handleSheetTouchCancel,
    };
};

export default useSheetHandlers;
